/**
 * 账户管理器
 * 负责管理账户信息，提供获取、更新等功能
 * @author 杨俊朗
 */
import { useEffect, useState } from "react";
import { canUseLocalStorage } from "@/utils/canuseLocalStorage";
import { account, type AccountData } from "@/services/account";
import { logger } from "@/utils/logger";
import analytics from "@/utils/analytics";
import { iamTokenManager } from "./iam-token-manager";

export class IamAccountManager {
  private static instance: IamAccountManager;
  private readonly STORAGE_KEY = "MIUTA_ACCOUNT";
  // 依赖的管理器
  private readonly iamTokenManager = iamTokenManager;
  // 状态
  private store: AccountData | null = null;

  // 单例模式
  public static getInstance(): IamAccountManager {
    if (!IamAccountManager.instance) {
      IamAccountManager.instance = new IamAccountManager();
    }
    return IamAccountManager.instance;
  }

  // 初始化
  public async init() {
    // 没有token不需要初始化
    if (!this.iamTokenManager.getToken()) {
      this.update(null);
      logger.info("No token found, skipping account initialization.");
      return;
    }
    // 优先从缓存获取数据
    if (canUseLocalStorage()) {
      const cache = localStorage.getItem(this.STORAGE_KEY);
      try {
        this.update(cache ? JSON.parse(cache) : null);
        logger.info("Account data loaded from cache:", this.store);
      } catch (e) {
        this.update(null);
        logger.error("Failed to parse account data:", e);
        throw e;
      }
    }
    // 后台同步数据
    try {
      const data = await account();
      this.update(data);
      logger.info("Account data fetched successfully:", this.store);
    } catch (e) {
      this.update(null);
      logger.error("Failed to fetch account data:", e);
      throw e;
    }
    // 走完整个流程才算初始化成功
    if (this.store) {
      analytics.setUserId(this.store.email);
      analytics.trackRegisteredUserVisit({
        userId: this.store.id,
        email: this.store.email,
      });
    }
  }

  // 更新账户信息
  public update(value: AccountData | null): void {
    this.store = value;
    this.dispatch();
    if (!canUseLocalStorage()) {
      return;
    }
    if (this.store) {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(value));
    } else {
      localStorage.removeItem(this.STORAGE_KEY);
    }
  }

  // 获取账户信息
  public get(): AccountData | null {
    return this.store;
  }

  // 清除账户信息
  public clear(): void {
    // 重置初始化状态
    this.update(null);
  }

  // 添加监听器
  public listen(listener: (e: CustomEvent<AccountData | null>) => void) {
    window.addEventListener(this.STORAGE_KEY, listener);
    // 返回取消监听的函数
    return () => {
      window.removeEventListener(this.STORAGE_KEY, listener);
    };
  }

  // 触发监听回调
  private dispatch() {
    // 触发自定义事件（保持向后兼容）
    const event = new CustomEvent<AccountData>(this.STORAGE_KEY, {
      detail: this.store,
    });
    window.dispatchEvent(event);
  }
}

const useAccountData = () => {
  const [account, setAccount] = useState<AccountData | null>(iamAccountManager.get());

  // 监听账户变化
  useEffect(() => {
    return iamAccountManager.listen((e: CustomEvent<AccountData | null>) => {
      setAccount(e.detail);
    });
  }, []);

  return {
    account: {
      ...account,
      // 测试用
      // nowIsSubscription: 1,
      // vipType: 1,
    },
  };
};

export const iamAccountHooks = {
  useData: useAccountData,
};

export const iamAccountManager = IamAccountManager.getInstance();
